import useAuth from "./useAuth";
import useTerms from "./useTerms";
import { useHasPermission } from "./useHasPermission";
import { useGetExaminationByAppointmentQuery } from "../store/examinationApi";

const idOf = (v) => (v && typeof v === "object" ? v._id : v);

/**
 * Randevu drawer'ındaki muayene butonu — sunucudaki kurallarla birebir:
 * - Muayene Et / Devam Et: `examinations:create` + randevunun doktoru (Admin her zaman).
 * - Görüntüle / Özet: `examinations:read` + (randevunun doktoru ya da
 *   `appointments:read-all`). Yönetici doktoru olmasa da görür.
 * Yetkisi olmayan kişiye buton hiç gösterilmez. Tamamlanmış randevunun muayene
 * kaydı yoksa (ör. süresi geçince otomatik tamamlanan) özet butonu da çıkmaz.
 *
 * @returns {{ mode: "start"|"resume"|"view"|null, label: string|null, variant: string, note?: string }}
 */
export default function useExaminationAction(appointment) {
  const { auth } = useAuth();
  const terms = useTerms();
  const canCreate = useHasPermission("examinations:create");
  const canRead = useHasPermission("examinations:read");
  const canReadAll = useHasPermission("appointments:read-all");

  const isAdmin = auth?.role === "Admin";
  const isOwn = String(idOf(appointment?.doctor) || "") === String(auth?._id || "");
  const canDo = canCreate && (isOwn || isAdmin);
  const canView = canRead && (isOwn || canReadAll || isAdmin);
  const status = String(appointment?.status || "").toLowerCase();

  // Yalnız özet butonu çıkacaksa muayene kaydının varlığını sor.
  const needsLookup = Boolean(appointment?._id) && status === "completed" && canView;
  const { data: found, isFetching } = useGetExaminationByAppointmentQuery(
    { token: auth?.accessToken, appointmentId: appointment?._id },
    { skip: !needsLookup || !auth?.accessToken }
  );

  const none = { mode: null, label: null, variant: "confirm" };
  if (!appointment) return none;

  if (status === "scheduled" && canDo) return { mode: "start", label: terms.examDo, variant: "confirm" };
  if (status === "in_examination") {
    if (canDo) return { mode: "resume", label: `${terms.exam}ye Devam Et`, variant: "save" };
    if (canView) return { mode: "view", label: `${terms.examAcc} Görüntüle`, variant: "edit" };
  }
  if (status === "completed" && canView) {
    if (isFetching || !found) return none;
    if (!found?.data?.examination) {
      return { ...none, note: `Bu randevu için ${terms.examLower} yapılmamış.` };
    }
    return { mode: "view", label: `${terms.exam} Özetini Gör`, variant: "edit" };
  }
  return none;
}
